import { useCallback } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { localStorage } from '@change-corgi/core/storage';
import { useNavigate } from '@change-corgi/core-react/router';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import {
	DraftClient,
	findOrCreateDraftAuthorId,
	InMemoryAdapter,
	LocalStorageAdapter,
} from '#src/app/shared/api/petitionDraft';
import { generateAiPetitionImage, type PetitionAngleData } from '#src/app/shared/api/socialStarter';
import { useQueryParamValue } from '#src/app/shared/hooks/location';
import { isLoaded, isLoading } from '#src/app/shared/utils/async';
import { appendSourceLocationToSapParams, appendVideoConsentToSapParams } from '#src/app/shared/utils/sap';
import { formatAngleDescriptionForSapEditor } from '#src/app/shared/utils/socialStarter';

import type { UseClaimPetitionPageData } from './types';

type UseClaimPetitionOptions = {
	readonly draft: PetitionAngleData;
	readonly pageData: UseClaimPetitionPageData;
	readonly returnPath?: string;
	readonly fallbackErrorMessage?: string;
	readonly keepLoadingAfterSuccess?: boolean;
	/**
	 * Omitted entirely by flows with no video-consent concept (e.g. ambassador drafts), which is
	 * distinct from passing `false` — see the `sapParams` handling below.
	 */
	readonly videoConsent?: boolean;
};

/**
 * Claims a social-starter-style petition angle draft into a full "start a petition" (SAP) draft
 * and navigates to the SAP preview once it's created.
 *
 * Generates an AI petition image from the angle, persists a `user_draft` via the petition-draft
 * API, then navigates to `/start-a-petition/preview` with the params (angle token, user handle,
 * source location, video consent) SAP needs to resume from that draft. Page-agnostic by design —
 * see {@link UseClaimPetitionPageData} — so it can be reused by any flow that shares this
 * "claim an angle draft into SAP" behavior (currently social starter and ambassador drafts).
 */
export function useClaimPetition({
	draft,
	pageData,
	returnPath,
	fallbackErrorMessage = 'claim_petition_failed',
	keepLoadingAfterSuccess = true,
	videoConsent,
}: UseClaimPetitionOptions): UseClaimPetitionReturn {
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const urlSourceLocation = useQueryParamValue('source_location');
	const utilityContext = useUtilityContext();
	const { username, token: resolvedToken, sourceLocation } = pageData;
	const sapParams = new URLSearchParams();
	if (returnPath) sapParams.set('return_to', returnPath);
	sapParams.set('angle_token', resolvedToken);
	if (username) sapParams.set('user_handle', username);
	appendSourceLocationToSapParams(sapParams, sourceLocation, urlSourceLocation);
	// `video_consent` must be entirely absent from the SAP params when the caller has no
	// video-consent concept, not present with a `false` value.
	if (videoConsent !== undefined) appendVideoConsentToSapParams(sapParams, videoConsent);
	const sapUrl = `/start-a-petition/preview?${sapParams.toString()}`;

	const { mutate, error, ...mutationState } = useMutation({
		mutationFn: async () => {
			const authorId = findOrCreateDraftAuthorId({ storage: localStorage, queryClient });
			const draftClient = new DraftClient([new LocalStorageAdapter(localStorage), new InMemoryAdapter(queryClient)]);

			const imageJob = await generateAiPetitionImage(
				{ mainPrompt: draft.recommendation, personalStory: draft.why },
				utilityContext,
			);

			await draftClient.createDraft(authorId, {
				type: 'user_draft',
				petitionDraft: {
					ask: draft.ask,
					description: formatAngleDescriptionForSapEditor(draft.description),
				},
				metadata: {
					imageJobId: imageJob?.responseId ?? null,
					imageGenerationCount: imageJob ? 1 : 0,
				},
			});
		},
		onSuccess: () => {
			navigate(sapUrl);
		},
		onError: (err) => {
			void utilityContext.errorReporter.report({
				error: err instanceof Error ? err : new Error(fallbackErrorMessage, { cause: err }),
			});
		},
	});

	const startPetition = useCallback(() => {
		mutate();
	}, [mutate]);

	// Stay in the loading state once the mutation succeeds: `onSuccess` triggers a
	// `navigate('/start-a-petition/preview')`, but react-query has already flipped
	// the mutation status back to `success` (loading -> false) by the time React
	// re-renders. Without this, the social starter page flashes back into view for
	// a frame between the loader and the SAP route. Keeping loading=true through
	// `success` lets the loader stay on screen until the page unmounts on navigate.
	const loading = isLoading(mutationState) || (keepLoadingAfterSuccess && isLoaded(mutationState));

	return {
		data: { loading, error },
		actions: { startPetition },
	};
}

type UseClaimPetitionReturn = ModelHookResult<{ loading: boolean; error: Error | null }, { startPetition: () => void }>;
